/**
 * 新患者周上限（Patient limits）。
 *
 * 后端契约见飞书《Patient Booking Limit — 前端对接文档》。两个要点：
 *  - 上限是 **practice 级** 的一份数据。Practice View 和 My Marketing 看到的范围不同，
 *    改的是同一个值 —— 任一处保存后，另一处的数据即失效。
 *  - 时间一律 epoch 毫秒，配合 timezone 字段格式化；不要用浏览器时区推算「下周一」。
 */

/** 目前仅 WEEKLY。后端按周期类型分行存储，将来会有 DAILY / MONTHLY。 */
export type TBookingLimitPeriodType = 'WEEKLY';

/**
 * 按判定顺序声明：后端自上而下匹配，命中即返回。
 * 顺序本身就是逻辑，渲染时请按状态分支，不要用 usedCount >= limitCount 自行推断是否暂停。
 */
export enum EBookingLimitListingState {
    /** 整个 practice 被暂停或下架，周上限此时无意义 */
    PRACTICE_PAUSED = 'PRACTICE_PAUSED',
    /** 从未上架，上限控件应置灰 */
    NOT_LISTED = 'NOT_LISTED',
    /**
     * 因**其他原因**暂停，期间发生过一次恢复上架尝试（人工或自动，如欠费付清后的自动恢复），
     * 但当时仍超限而被扣住。上限解除时会重新评估，**不保证一定上架** ——
     * 原因仍在则直接转为 PAUSED_OTHER。文案不可承诺自动恢复。
     */
    PAUSED_BY_LIMIT_HELD = 'PAUSED_BY_LIMIT_HELD',
    /** 被本功能暂停，展示「调高上限」主操作 */
    PAUSED_BY_LIMIT = 'PAUSED_BY_LIMIT',
    /** 被其他原因暂停（Ops / 执照 / 欠费），沿用现有 listing banner，不展示上限操作 */
    PAUSED_OTHER = 'PAUSED_OTHER',
    ACTIVE = 'ACTIVE',
}

export interface IBookingLimitRule {
    periodType: TBookingLimitPeriodType;
    enabled: boolean;
    /** 关闭时保留原值，便于再次开启时回填；仅从未设置过时为 null */
    limitCount: number | null;
    /** 本周期已用新患者数。practice 视图的规则层为 null，用量在成员维度 */
    usedCount: number | null;
    /** epoch 毫秒 */
    periodStartAt: number;
    /** epoch 毫秒，下次重置时刻 */
    periodEndAt: number;
}

export interface IBookingLimitVo {
    /** 唯一的可编辑判据。不要用角色或 practice 规模推断 */
    canEdit: boolean;
    /** 周期计算所用的 IANA 时区（practice owner 的时区，未必是当前用户的） */
    timezone: string;
    listingState: EBookingLimitListingState;
    /** 本周 lead gen fee 实际花费（美分），排除取消与退款；与上限判定无关 */
    weeklySpentCents: number;
    rules: IBookingLimitRule[];
}

export interface IPracticeBookingLimitMemberUsage {
    periodType: TBookingLimitPeriodType;
    usedCount: number;
}

export interface IPracticeBookingLimitMember {
    /** 姓名头像需另 join team member 接口 */
    providerId: number;
    weeklySpentCents: number;
    listingState: EBookingLimitListingState;
    usage: IPracticeBookingLimitMemberUsage[];
}

export interface IPracticeBookingLimitSummary {
    memberCount: number;
    /** 含 PAUSED_BY_LIMIT 与 PAUSED_BY_LIMIT_HELD */
    pausedByLimitCount: number;
    /** 还差一单就触顶的活跃成员数 */
    oneAwayCount: number;
    weeklySpentCents: number;
    /**
     * 汇总带的「Marketplace bookings this week」= 各成员 WEEKLY usedCount 之和。
     *
     * 口径有两处反直觉的地方，所以由后端算：取消的账单照算（取消不退还名额），
     * 而 weeklySpentCents 恰好相反会排除。
     *
     * **可选**：这个字段 2026-09-17 才加（boxpractice MR !3188），
     * 三个环境部署完成前接口不会返回它。调用方必须兜底，否则这一格是空白。
     * 三环境都上线后可以改成必填并删掉兜底。
     */
    weeklyNewPatientCount?: number;
}

export interface IPracticeBookingLimitVo {
    organizationId: number;
    canEdit: boolean;
    timezone: string;
    rules: IBookingLimitRule[];
    summary: IPracticeBookingLimitSummary;
    members: IPracticeBookingLimitMember[];
}

/**
 * 保存/预览的请求体。
 *
 * 写成判别联合而不是 `limitCount?: number`：开启时后端必填，漏传直接是运行时
 * `1000 Limit must be between 1 and 999.`。用类型在编译期就挡掉这一类调用。
 */
export type IBookingLimitSaveReq =
    | {
          periodType: TBookingLimitPeriodType;
          enabled: true;
          /** 1–999 */
          limitCount: number;
      }
    | {
          periodType: TBookingLimitPeriodType;
          enabled: false;
          /** 关闭时可省略，后端保留原值，便于再次开启时回填 */
          limitCount?: number;
      };

export interface IBookingLimitPreviewItem {
    providerId: number;
    /** 仅 willPause 有值；willResume 恒为 null */
    usedCount: number | null;
}

export interface IBookingLimitSavePreviewVo {
    /** 保存后会被暂停的成员 */
    willPause: IBookingLimitPreviewItem[];
    /**
     * 本次改动能真正放出来的成员。
     * 比「所有低于新上限的人」要窄：同时有欠费 / 扣款失败的成员会被交接给那个原因，
     * 不会恢复上架，因此被刻意排除 —— 确认框不要承诺他们会回来。
     */
    willResume: IBookingLimitPreviewItem[];
}

export interface IBookingLimitChange {
    /** epoch 毫秒 */
    changedAt: number;
    operatorUserId: number;
    operatorName: string;
    /** PROVIDER / GROUP_OWNER / ADMIN，操作当时的快照值 */
    operatorRole: string;
    periodType: TBookingLimitPeriodType;
    /** "20" / "Unlimited"；首条为 null，展示为 – */
    previousValue: string | null;
    newValue: string;
}
